import { useEffect, useMemo } from "react";
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetCoInvestLpPermissions } from "@vega-api/generated/coinvest-lp-permissions/coinvest-lp-permissions";
import { useGetDealOpportunityLimitedPartnerViewV3 } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { DealOpportunityStatusEnum } from "@vega-api/generated/types/dealOpportunityStatusEnum";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { TabNavigation } from "@vega-core/components/Layout/TabNavigation";
import { type Tab } from "@vega-core/models/core.type";
import { m } from "@vega-core/paraglide/messages";
import { useDefaultCurrency } from "@vega-core/utils/formatMoney";
import {
  trackCoInvestDealOpportunityViewed,
  trackCoInvestDealTabViewed,
} from "@vega-core/utils/posthog/coInvestAnalytics";
import { LPPageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import {
  CoInvestmentFullPaths,
  CoInvestmentLpPaths,
  CommonPaths,
} from "@vega-modules/paths";

import { CoInvestmentLayout } from "../Layout/CoInvestmentLayout";
import { DataRoomTab } from "../tabs/DataRoomTab";
import { OverviewTab } from "../tabs/OverviewTab";
import { PerformanceReportTab } from "../tabs/PerformanceReportTab";
import { TermsTab } from "../tabs/TermsTab";

export const CoInvestmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: dealOpportunityId = "" } = useParams<{ id: string }>();
  // const { formatAssetClassName } = useFormatAssetClass();

  const {
    data,
    isLoading: isLoadingDealOpportunity,
    isError,
  } = useGetDealOpportunityLimitedPartnerViewV3(dealOpportunityId, {
    query: {
      enabled: !!dealOpportunityId,
      select: (data) => data.data,
    },
  });
  const { data: lpPermissions, isLoading: isLoadingLpPermissions } =
    useGetCoInvestLpPermissions(
      {
        dealOpportunityId,
      },
      {
        query: {
          select: (data) => data.data,
        },
      },
    );

  const { featureFlag, isLoading: isFeatureFlagLoading } = useFeatureFlag();

  const isLoading =
    isLoadingDealOpportunity || isLoadingLpPermissions || isFeatureFlagLoading;

  const dealData = data?.deal;
  const canViewSensitiveOpportunityDetails =
    lpPermissions?.canViewSensitiveOpportunityDetails;
  const canUpdateSensitiveOpportunityDetails =
    lpPermissions?.canUpdateSensitiveOpportunityDetails;

  const hasAccessToDataRoom = data?.hasDataRoomAccess;
  const dataRooms = data?.deal.dealDataRooms;
  const vegaDataRoomId = data?.deal.vegaDataRooms?.[0]?.id;
  const hasIndicatedInterest =
    !!data?.dealOpportunity.indicationOfInterestAmount;
  const hasRejectedOffer = data?.isDealOpportunityRejected ?? false;
  const indicatedInterestAmount =
    data?.dealOpportunity.indicationOfInterestAmount;
  const commitmentAmount = data?.dealOpportunity.commitmentAmount;
  const allocationAmount = data?.dealOpportunity.allocationAmount;
  const allocationAcceptedAt = data?.dealOpportunity.allocationAcceptedAt;
  const isDealOpportunityClosed = data?.dealOpportunity.isClosed;
  // Deal-level close (fundraising finished) — distinct from the per-LP
  // opportunity close above; locks all LP mutations
  const isDealClosed = data?.deal.status === DealStatusEnum.CLOSED;
  const isCommitted =
    data?.dealOpportunity.status === DealOpportunityStatusEnum.COMMITTED;
  const isReportingEnabled =
    featureFlag[FeatureFlag.COINVEST_REPORTING_VD_26152];
  const showPerformanceReport =
    isReportingEnabled && isDealClosed && isCommitted;
  const isNdaSigned = data?.ndaStatuses?.isSigned ?? false;

  const ticketSize = data?.dealOpportunity.ticketSize;
  const defaultCurrency = useDefaultCurrency();
  const dealCurrency = dealData?.currency ?? defaultCurrency;
  const projectName = dealData?.name;
  const targetAmount = dealData?.targetAmount;
  // Temporary commented out because not all GP (e.g. Apollo) will have these values: https://vegainvestments.atlassian.net/browse/VD-18007
  // const tags = dealData
  //   ? ([
  //       convertToTitleCase(dealData.sector),
  //       convertToTitleCase(dealData.region),
  //       formatAssetClassName(dealData.assetClass),
  //     ].filter(Boolean) as string[])
  //   : undefined;

  const viewedDealId = data?.dealOpportunity.dealId;

  useEffect(() => {
    if (!dealOpportunityId || !viewedDealId) return;

    trackCoInvestDealOpportunityViewed({
      dealOpportunityId,
      dealId: viewedDealId,
    });
  }, [dealOpportunityId, viewedDealId]);

  const tabs: Tab[] = useMemo(
    () => [
      {
        index: CoInvestmentLpPaths.Overview,
        title: m.coInvestments_overview_title(),
        component: <OverviewTab data={data} isLoading={isLoading} />,
        isActive: !!matchPath(
          { path: CoInvestmentFullPaths.CoInvestmentLpOverview },
          location.pathname,
        ),
        disabled: false,
      },
      {
        index: CoInvestmentLpPaths.DataRoom,
        title: m.coInvestments_dataRoom_title(),
        component: (
          <DataRoomTab
            dataRooms={dataRooms}
            dealId={data?.dealOpportunity.dealId}
            dealOpportunityId={dealOpportunityId}
            vegaDataRoomId={vegaDataRoomId}
          />
        ),
        isActive: !!matchPath(
          { path: CoInvestmentFullPaths.CoInvestmentLpDataRoom },
          location.pathname,
        ),
        disabled: !hasAccessToDataRoom,
        tooltip: hasAccessToDataRoom
          ? undefined
          : m.coInvestments_dataRoom_tooltip(),
      },
      ...(canViewSensitiveOpportunityDetails
        ? [
            {
              index: CoInvestmentLpPaths.Terms,
              title: m.coInvestments_terms_title(),
              component: <TermsTab data={data} />,
              isActive: !!matchPath(
                { path: CoInvestmentFullPaths.CoInvestmentLpTerms },
                location.pathname,
              ),
              disabled: !hasAccessToDataRoom,
              tooltip: hasAccessToDataRoom
                ? undefined
                : m.coInvestments_terms_tooltip(),
            },
          ]
        : []),
      ...(showPerformanceReport
        ? [
            {
              index: CoInvestmentLpPaths.PerformanceReport,
              title: m.coInvestments_performanceReport_title(),
              component: (
                <PerformanceReportTab dealOpportunityId={dealOpportunityId} />
              ),
              isActive: !!matchPath(
                { path: CoInvestmentFullPaths.CoInvestmentLpPerformanceReport },
                location.pathname,
              ),
              disabled: false,
            },
          ]
        : []),
    ],
    [
      location.pathname,
      data,
      isLoading,
      dealOpportunityId,
      hasAccessToDataRoom,
      canViewSensitiveOpportunityDetails,
      showPerformanceReport,
      dataRooms,
      vegaDataRoomId,
    ],
  );

  if (isError) {
    return <Navigate replace to={CommonPaths.NotFound} />;
  }

  const overviewPath = CoInvestmentFullPaths.CoInvestmentLpOverview.replace(
    ":id",
    dealOpportunityId,
  );

  // Block gated routes on direct navigation. Flags settle only after load,
  // so wait for data before redirecting. Terms mirrors the tab-unlock condition.
  if (!isLoading && data) {
    const isOnDataRoomRoute = !!matchPath(
      { path: CoInvestmentFullPaths.CoInvestmentLpDataRoom },
      location.pathname,
    );
    const isOnTermsRoute = !!matchPath(
      { path: CoInvestmentFullPaths.CoInvestmentLpTerms },
      location.pathname,
    );

    if (isOnDataRoomRoute && hasAccessToDataRoom === false) {
      return <Navigate replace to={overviewPath} />;
    }

    if (
      isOnTermsRoute &&
      (!canViewSensitiveOpportunityDetails || hasAccessToDataRoom === false)
    ) {
      return <Navigate replace to={overviewPath} />;
    }

    const isOnPerformanceReportRoute = !!matchPath(
      { path: CoInvestmentFullPaths.CoInvestmentLpPerformanceReport },
      location.pathname,
    );

    if (isOnPerformanceReportRoute && !showPerformanceReport) {
      return <Navigate replace to={overviewPath} />;
    }
  }

  const handleTabChange = (newActiveTab: string) => {
    if (!dealOpportunityId) return;

    const fullPathByTab: Record<string, string> = {
      [CoInvestmentLpPaths.DataRoom]:
        CoInvestmentFullPaths.CoInvestmentLpDataRoom,
      [CoInvestmentLpPaths.Terms]: CoInvestmentFullPaths.CoInvestmentLpTerms,
      [CoInvestmentLpPaths.PerformanceReport]:
        CoInvestmentFullPaths.CoInvestmentLpPerformanceReport,
    };
    const tabPath = (
      fullPathByTab[newActiveTab] ??
      CoInvestmentFullPaths.CoInvestmentLpOverview
    ).replace(":id", dealOpportunityId);

    // TODO(VD-28690): interim guard. TabNavigation fires onChange on mount,
    // which pushes a duplicate same-URL history entry and breaks single-step
    // browser-back. Remove once the mount-time onChange is fixed at the
    // component level.
    if (tabPath === location.pathname) return;

    trackCoInvestDealTabViewed({ dealOpportunityId, tab: newActiveTab });
    navigate(tabPath + location.hash);
  };

  return (
    <LPPageContainerWithNavigation showNavbar={false}>
      <CoInvestmentLayout
        allocationAcceptedAt={allocationAcceptedAt}
        allocationAmount={allocationAmount}
        canUpdateSensitiveOpportunityDetails={
          canUpdateSensitiveOpportunityDetails
        }
        canViewSensitiveOpportunityDetails={canViewSensitiveOpportunityDetails}
        commitmentAmount={commitmentAmount}
        dealCurrency={dealCurrency}
        dealId={data?.dealOpportunity.dealId}
        dealOpportunityId={dealOpportunityId}
        hasAccessToDataRoom={hasAccessToDataRoom}
        hasIndicatedInterest={hasIndicatedInterest}
        hasRejectedOffer={hasRejectedOffer}
        indicatedInterestAmount={indicatedInterestAmount}
        isDealClosed={isDealClosed}
        isDealOpportunityClosed={isDealOpportunityClosed}
        isLoading={isLoading}
        isNdaSigned={isNdaSigned}
        projectName={projectName}
        // Temporary not passing tags because not all GP (e.g. Apollo) will have these values: https://vegainvestments.atlassian.net/browse/VD-18007
        tags={[]}
        targetAmount={targetAmount}
        ticketSize={ticketSize}
      >
        {isLoading ? null : (
          <TabNavigation
            fullWidth={false}
            onChange={handleTabChange}
            tabs={tabs}
            transparentBG
          />
        )}
      </CoInvestmentLayout>
    </LPPageContainerWithNavigation>
  );
};
